import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../utils/api';
import { errorText } from '../../../utils/errorText';
import { displayName } from '../../../utils/userLabel';
import StoragePanel, { shortLocation } from '../../specimens/StoragePanel';
import { canDo } from '../stages';
import { liveSlant, submissionKeys, unbankedSlant } from '../quickActions';
import { Code, Day, ReviewDue, SlantPill, Taxon } from '../cells';
import * as S from '../../specimens/styles';
import { analysisLine } from './MicroscopyTab';

/*
 * Bank location: how the specimen itself is kept (StoragePanel, the
 * specimen page's own, saved through PUT /specimens/{id}), then each slant
 * -- where it is, when and as what it was preserved, whether its review is
 * due -- and every DNA tube with a place. Above the slants, the isolate's spore
 * metrics: the newest Spore analysis saved on any of its micrographs.
 *
 * Slants not yet stored go into the bank together ("Deposit to bank", one
 * place and one preservation record for all of them); a single slant's
 * place is set, and a live one revived, from its own buttons.
 *
 * StoragePanel speaks the specimens catalogue: it gets that namespace's t,
 * not the hub's, so its "cancel" or "storage.title" can never resolve to a
 * hub string of the same name.
 */
export default function BankTab({ item, dossier, user, onAction, onStorageSaved }) {
  const { t } = useTranslation(['isolates', 'specimens', 'slants', 'plates', 'common', 'dna']);
  const { t: tSpec } = useTranslation('specimens');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [keys] = useState(() => submissionKeys('hub-storage'));
  const specimen = dossier.specimen;
  const slants = dossier.slants || [];
  const tubes = (dossier.dna || []).filter((d) => d.storage_unit);
  const loose = slants.some(unbankedSlant) && canDo(user, 'deposit', item);
  const spores = (dossier.spore_analyses || [])[0];

  const save = async (body) => {
    setBusy(true);
    setError('');
    const path = `/specimens/${item.specimen_id}`;
    try {
      await api.put(path, body, { headers: { 'Idempotency-Key': keys.keyFor(path, body) } });
      keys.reset();
      onStorageSaved?.(item);
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {specimen && (
        <StoragePanel specimen={specimen} t={tSpec} busy={busy} error={error} onSave={save}
          canEdit={canDo(user, 'specimenStorage') && specimen.status === 'active'} />
      )}

      {spores && (
        <p data-testid="hub-bank-spores" style={{ fontSize: 13, margin: '12px 0 0' }}>
          <strong>{t('bank.sporeMetrics')}:</strong> <bdi dir="ltr">{analysisLine(spores, t, 'bank')}</bdi>
        </p>
      )}

      <h4 style={{ ...S.sectionTitle, marginTop: 16 }}>{t('bank.slants')}</h4>
      {loose && (
        <div className="hub-tab-actions">
          <button type="button" style={S.ghostBtn} onClick={() => onAction('deposit', item)}
            title={t('tip.action.deposit')} data-testid="hub-bank-deposit">{t('action.deposit')}</button>
        </div>
      )}
      {slants.length === 0 && <p style={S.hint}>{t('bank.noSlants')}</p>}
      <ul className="hub-records">
        {slants.map((s) => (
          <li key={s.slant_id} className="hub-record" data-testid={`hub-slant-${s.slant_id}`}>
            <div className="hub-record__head">
              <Code strong>{s.slant_code}</Code>
              {s.slant_role && <span style={S.hint}>{t(`slants:slantRole.${s.slant_role}`)}</span>}
              <SlantPill status={s.status} t={t} />
              {s.review_due && <ReviewDue t={t} />}
            </div>
            <div style={S.hint}>
              {shortLocation(s, tSpec) || t('bank.notStored')}
              {s.preserved_at && <> · {t('bank.preservedOn')} <Day value={s.preserved_at} /></>}
              {s.preserved_by_name && <> · {displayName(s.preserved_by_name, t)}</>}
              {s.preserved_organism && <> · <Taxon name={s.preserved_organism} /></>}
            </div>
            <div className="hub-record__actions">
              {/* Nothing is grown from a discarded or archived tube. */}
              {['setStorage', 'logReview', 'revive']
                .filter((key) => canDo(user, key, item) && (key !== 'revive' || liveSlant(s)))
                .map((key) => (
                  <button key={key} type="button" style={smallBtn} onClick={() => onAction(key, item, { slantId: s.slant_id })}
                    title={t(`tip.action.${key}`)} data-testid={`hub-slant-${key}-${s.slant_id}`}>{t(`action.${key}`)}</button>
                ))}
            </div>
          </li>
        ))}
      </ul>

      {tubes.length > 0 && (
        <>
          <h4 style={{ ...S.sectionTitle, marginTop: 16 }}>{t('bank.dnaTubesTitle')}</h4>
          <ul className="hub-records">
            {tubes.map((d) => (
              <li key={d.dna_id} className="hub-record">
                <Code strong>{d.isolate_code}</Code> <span style={S.hint}>{shortLocation(d, tSpec)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const smallBtn = { ...S.ghostBtn, padding: '4px 10px', fontSize: 13 };
